// ─────────────────────────────────────────────────────────────
// 사본: E:/loginhs-verify/frontend/src/audit/originToFta.js (verify.tlogin.net 저장소, 2026-09-23 분리) — 고칠 때 양쪽을 본다.
// originToFta.js — 원산지(국가) → 적용 가능 FTA 협정 + 세율 선택 엔진 (P3)
//   rates_full(코드별 {value,label}) + 원산지 → 적용가능 세율 가려내고 최저 추천.
//   순수함수(jest-able). UI(HskDetailPanel) 와 분리.
// ─────────────────────────────────────────────────────────────

// 원산지 국가 → 적용 가능한 FTA 코드 prefix 목록.
//   rates_full 의 FTA 코드(FEU1/FUS1/FRCCN1...)를 prefix 로 매칭. 한 국가가 복수 협정
//   대상이면 전부 후보(엔진이 최저 선택). 선택1(*1) 우선은 엔진에서 처리.
//   ⚠ 회원국 목록은 대표 교역국 위주(전수 아님) — 추후 보강 가능.
export const ORIGIN_FTA = {
  // ── 유럽 (EU / 영국 / EFTA) ──
  독일: ["FEU"],
  프랑스: ["FEU"],
  이탈리아: ["FEU"],
  스페인: ["FEU"],
  네덜란드: ["FEU"],
  벨기에: ["FEU"],
  폴란드: ["FEU"],
  스웨덴: ["FEU"],
  오스트리아: ["FEU"],
  아일랜드: ["FEU"],
  체코: ["FEU"],
  덴마크: ["FEU"],
  핀란드: ["FEU"],
  포르투갈: ["FEU"],
  헝가리: ["FEU"],
  슬로바키아: ["FEU"],
  영국: ["FGB"],
  스위스: ["FEF"],
  노르웨이: ["FEF"],
  아이슬란드: ["FEF"],
  // ── 미주 ──
  미국: ["FUS"],
  캐나다: ["FCA"],
  칠레: ["FCL"],
  페루: ["FPE"],
  콜롬비아: ["FCO"],
  코스타리카: ["FCECR"],
  파나마: ["FCEPA"],
  온두라스: ["FCEHN"],
  니카라과: ["FCENI"],
  엘살바도르: ["FCESV"],
  // ── 아시아·태평양 (FTA + RCEP) ──
  중국: ["FCN", "FRCCN"],
  일본: ["FRCJP"],
  베트남: ["FVN", "FAS", "FRCAS"],
  싱가포르: ["FSG", "FAS", "FRCAS"],
  태국: ["FAS", "FRCAS"],
  말레이시아: ["FAS", "FRCAS"],
  인도네시아: ["FID", "FAS", "FRCAS"],
  필리핀: ["FAS", "FRCAS"],
  캄보디아: ["FKH", "FAS", "FRCAS"],
  미얀마: ["FAS", "FRCAS"],
  라오스: ["FAS", "FRCAS"],
  브루나이: ["FAS", "FRCAS"],
  호주: ["FAU", "FRCAU"],
  뉴질랜드: ["FNZ", "FRCNZ"],
  인도: ["FIN"],
  // ── 중동 ──
  터키: ["FTR"],
  UAE: ["FAE"],
  이스라엘: ["FIL"],
};

// 드롭다운용 국가 목록(주요 교역국 우선). FTA 비대상 국가도 기본/WTO 계산 위해 포함.
export const ORIGIN_COUNTRIES = [
  ...Object.keys(ORIGIN_FTA),
  // FTA 비대상(기본/WTO 만) — 참고용 대표
  "러시아",
  "대만",
  "홍콩",
  "사우디아라비아",
  "남아프리카공화국",
  "멕시코",
  "브라질",
];

// ISO-2 → 한글 국명 별칭 — 대량 세번분류 원본 COO 컬럼("US","CN"…) 해석용.
// ORIGIN_FTA 전 키 + ORIGIN_COUNTRIES 비FTA국 커버. 미수록(KR 자국 등)=null.
export const ISO2_ORIGIN = {
  DE: "독일",
  FR: "프랑스",
  IT: "이탈리아",
  ES: "스페인",
  NL: "네덜란드",
  BE: "벨기에",
  PL: "폴란드",
  SE: "스웨덴",
  AT: "오스트리아",
  IE: "아일랜드",
  CZ: "체코",
  DK: "덴마크",
  FI: "핀란드",
  PT: "포르투갈",
  HU: "헝가리",
  SK: "슬로바키아",
  GB: "영국",
  CH: "스위스",
  NO: "노르웨이",
  IS: "아이슬란드",
  US: "미국",
  CA: "캐나다",
  CL: "칠레",
  PE: "페루",
  CO: "콜롬비아",
  CR: "코스타리카",
  PA: "파나마",
  HN: "온두라스",
  NI: "니카라과",
  SV: "엘살바도르",
  CN: "중국",
  JP: "일본",
  VN: "베트남",
  SG: "싱가포르",
  TH: "태국",
  MY: "말레이시아",
  ID: "인도네시아",
  PH: "필리핀",
  KH: "캄보디아",
  MM: "미얀마",
  LA: "라오스",
  BN: "브루나이",
  AU: "호주",
  NZ: "뉴질랜드",
  IN: "인도",
  TR: "터키",
  AE: "UAE",
  IL: "이스라엘",
  RU: "러시아",
  TW: "대만",
  HK: "홍콩",
  SA: "사우디아라비아",
  ZA: "남아프리카공화국",
  MX: "멕시코",
  BR: "브라질",
};

/** COO 원시값(ISO-2 또는 한글 국명) → ORIGIN_FTA 키 국명. 해석 불가=null(FTA Y/N 폴백). */
export function resolveOrigin(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  if (/^[A-Za-z]{2}$/.test(s)) return ISO2_ORIGIN[s.toUpperCase()] || null;
  if (
    Object.prototype.hasOwnProperty.call(ORIGIN_FTA, s) ||
    ORIGIN_COUNTRIES.includes(s)
  )
    return s;
  return null;
}

// WTO 비회원(드묾) — 대부분 회원이라 비회원만 명시. 비회원이면 WTO(C) 후보 제외.
const NON_WTO = new Set(["북한", "이란", "투르크메니스탄", "에리트레아"]);

// ─── 세율 선택 엔진 ───────────────────────────────────────────
// rates_full: { CODE: { value:number, label:string, raw:string }, ... }
// origin: 국가명(ORIGIN_FTA 키) 또는 임의 문자열.
// 반환: { applicable:[{code,label,value,kind}], recommended|null, basic, wto, has_origin_fta }
//   kind: 'basic'|'wto'|'fta'|'preference'. recommended = 후보 중 최저 value.
export function computeApplicableRates(ratesFull, origin) {
  const rf = ratesFull && typeof ratesFull === "object" ? ratesFull : {};
  const entry = (code) => {
    const e = rf[code];
    if (!e) return null;
    const v = typeof e.value === "number" ? e.value : null;
    return { code, label: e.label || code, value: v, raw: e.raw || null };
  };

  const applicable = [];

  // 1) 기본세율 A — 항상 적용
  const basic = entry("A");
  if (basic) applicable.push({ ...basic, kind: "basic" });

  // 2) WTO 협정세율 C — origin 이 WTO 회원이면 후보
  const wto = entry("C");
  const isWtoMember = !NON_WTO.has(origin);
  if (wto && isWtoMember) applicable.push({ ...wto, kind: "wto" });

  // 3) FTA — origin 의 협정 코드 prefix 와 매칭되는 rates_full 코드. 선택1(*1) 우선.
  const prefixes = ORIGIN_FTA[origin] || [];
  const seenFtaPrefix = new Set();
  for (const pre of prefixes) {
    // 같은 협정의 코드 중 선택1(끝 '1') 우선, 없으면 첫 매칭.
    const matches = Object.keys(rf).filter((c) => c.startsWith(pre));
    if (!matches.length) continue;
    const pick = matches.find((c) => /1$/.test(c)) || matches.sort()[0];
    const e = entry(pick);
    if (e && !seenFtaPrefix.has(pre)) {
      seenFtaPrefix.add(pre);
      applicable.push({ ...e, kind: "fta" });
    }
  }

  // 4) 특혜 — 북한산(U)·최빈국(R) 등. origin 이 해당군이면 후보.
  //    (간이: U=북한 origin, R=최빈국 목록 — MVP 는 명시 origin 만.)
  if (origin === "북한") {
    const u = entry("U");
    if (u) applicable.push({ ...u, kind: "preference" });
  }

  // recommended = value 있는 후보 중 최저. 동률이면 fta>preference>wto>basic.
  const order = { fta: 0, preference: 1, wto: 2, basic: 3 };
  const valued = applicable.filter((a) => a.value != null);
  let recommended = null;
  if (valued.length) {
    recommended = valued.slice().sort((a, b) => {
      if (a.value !== b.value) return a.value - b.value;
      return (order[a.kind] ?? 9) - (order[b.kind] ?? 9);
    })[0];
  }

  return {
    applicable,
    recommended,
    basic: basic || null,
    wto: wto && isWtoMember ? wto : null,
    has_origin_fta: prefixes.length > 0,
  };
}
